import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  HiMenuAlt3, 
  HiX, 
  HiChevronDown, 
  HiVideoCamera, 
  HiHome,
  HiOfficeBuilding,
  HiUserGroup,
  HiBriefcase,
  HiInformationCircle,
  HiClock
} from "react-icons/hi";
import { getLoggedInUser } from "../utils/authUser";

const Header = ({
  onAppointmentClick,
  onNurseAppointmentClick,
  onVideoConsultingClick,
  onApplyNurseClick,
  onApplyDoctorClick,
}) => {
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const checkUser = () => {
      setUser(getLoggedInUser());
    };
    checkUser();
    window.addEventListener("storage", checkUser);
    return () => window.removeEventListener("storage", checkUser);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target))
        setIsApplyOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(event.target))
        setIsUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    [
      "customer",
      "user",
      "userType",
      "userTypeLabel",
      "phone",
      "isLoggedIn",
      "token",
      "userId",
      "userName",
      "name",
      "loginTimestamp",
      "role",
      "roleSlug",
      "permissions",
      "systemUserId",
    ].forEach((key) => localStorage.removeItem(key));
    setUser(null);
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate("/login");
  };

  const navLinks = [
    {
      title: "Home",
      subtitle: "Back to home",
      href: "/",
      
    },
    {
      title: "Video Consulting",
      subtitle: "Find a Specialist",
      onClick: onVideoConsultingClick,
     
    },
    {
      title: "Hospitals",
      subtitle: "Find a Hospitals & Clinics",
      onClick: onAppointmentClick,
     
    },
    {
      title: "Nurses",
      subtitle: "Find a Your Nurse",
      onClick: onNurseAppointmentClick,
     
    },
    {
      title: "Pharmacies",
      subtitle: "Order products for pickup or delivery",
      href: "/pharmacies",
    },
    {
      title: "Book Driver",
      subtitle: "Delivery service",
      href: "/driver-booking",
    },
    {
      title: "Join Our Team",
      subtitle: "Apply as specialist",
      isDropdown: true,
    
    },
    {
      title: "About Us",
      subtitle: "Our mission",
      href: "/#about-us",
   
    },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${
        scrolled || isMobileMenuOpen
          ? "bg-white/95 backdrop-blur-md shadow-md py-1.5"
          : "bg-white py-2"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-2"
          >
            <img
              src="/caafi.png"
              alt="Logo"
              className="h-9 w-auto transition-transform active:scale-95"
            />
            <div className="h-6 w-px bg-gray-200 hidden md:block"></div>
          </Link>

          {/* DESKTOP NAVIGATION */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, idx) => {
              const IconComponent = link.icon;
              return (
                <div
                  key={idx}
                  className="relative"
                  ref={link.isDropdown ? dropdownRef : null}
                >
                  {link.isDropdown ? (
                    <button
                      onClick={() => setIsApplyOpen(!isApplyOpen)}
                      className="flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-gray-50 group"
                    >
                      <span className="text-gray-700 text-xs font-semibold group-hover:text-[#6CA9F5] transition-colors">
                        {link.title}
                      </span>
                      <HiChevronDown
                        className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${
                          isApplyOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (link.onClick) {
                          link.onClick();
                        } else if (link.href?.startsWith("/#")) {
                          window.location.href = link.href;
                        } else if (link.href) {
                          navigate(link.href);
                        }
                      }}
                      className="px-3 py-2 rounded-lg hover:bg-gray-50 group"
                    >
                      <span className="text-gray-700 text-xs font-semibold group-hover:text-[#6CA9F5] transition-colors">
                        {IconComponent && <IconComponent className="w-4 h-4 mr-1" />}
                        {link.title}
                      </span>
                    </button>
                  )}

                  {/* Apply Dropdown */}
                  {link.isDropdown && isApplyOpen && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 animate-in fade-in zoom-in-95">
                      <button
                        onClick={() => {
                          onApplyNurseClick();
                          setIsApplyOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-blue-50 hover:text-[#6CA9F5] transition-colors"
                      >
                        Apply as Nurse
                      </button>
                      <button
                        onClick={() => {
                          onApplyDoctorClick();
                          setIsApplyOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-blue-50 hover:text-[#6CA9F5] transition-colors"
                      >
                        Apply as Doctor
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* USER ACTIONS & MOBILE TOGGLE */}
        <div className="flex items-center gap-2">
          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-1.5 p-1 pr-3 bg-gray-50 border border-gray-100 rounded-full hover:bg-white hover:shadow-sm transition-all active:scale-95"
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-[#6CA9F5] to-blue-600 flex items-center justify-center text-white font-bold text-xs">
                  {user.full_name?.charAt(0).toUpperCase()}
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-[10px] font-bold text-gray-800 leading-none">
                    {user.full_name?.split(" ")[0]}
                  </p>
                  <p className="text-[8px] text-blue-500 font-semibold uppercase tracking-tighter">
                    Patient
                  </p>
                </div>
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 p-1.5 z-[70] animate-in slide-in-from-top-2">
                  <div className="px-2.5 py-1.5 border-b border-gray-50 mb-1">
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                      Account
                    </p>
                    <p className="text-xs font-semibold text-gray-700 truncate">
                      {user.phone}
                    </p>
                  </div>
                  <Link
                    to="/appointments/history"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="block px-2.5 py-2 text-xs font-semibold text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-all"
                  >
                    My History
                  </Link>
                  <Link
                    to="/pharmacies"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="block px-2.5 py-2 text-xs font-semibold text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-all"
                  >
                    Pharmacies & Orders
                  </Link>
                  <Link
                    to="/driver-booking"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="block px-2.5 py-2 text-xs font-semibold text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-all"
                  >
                    Book Driver
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-2.5 py-2 text-xs font-semibold text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden sm:flex items-center gap-1.5">
              <Link
                to="/login"
                className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-[#6CA9F5] transition-colors"
              >
                Log In
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 bg-[#6CA9F5] text-white text-xs font-bold rounded-full shadow-md hover:shadow-lg transition-all"
              >
                Create Account
              </Link>
            </div>
          )}

          {/* Hamburger Icon */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-xl bg-gray-50 text-gray-600 active:scale-90 transition-all"
          >
            {isMobileMenuOpen ? <HiX size={20} /> : <HiMenuAlt3 size={20} />}
          </button>
        </div>
      </div>

      {/* MOBILE DRAWER */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-xl transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen
            ? "max-h-[90vh] opacity-100 py-4"
            : "max-h-0 opacity-0 py-0"
        }`}
      >
        <div className="px-4 space-y-1">
          {navLinks.map((link, idx) => {
            const IconComponent = link.icon;
            return (
              <div key={idx}>
                {link.isDropdown ? (
                  <div className="bg-gray-50 rounded-xl p-3 mb-1">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                      Team Applications
                    </span>
                    <div className="flex flex-col gap-1 mt-2">
                      <button
                        onClick={() => {
                          onApplyNurseClick();
                          setIsMobileMenuOpen(false);
                        }}
                        className="text-left text-sm font-semibold text-gray-700 hover:text-[#6CA9F5] py-2"
                      >
                        Nurse Enrollment
                      </button>
                      <button
                        onClick={() => {
                          onApplyDoctorClick();
                          setIsMobileMenuOpen(false);
                        }}
                        className="text-left text-sm font-semibold text-gray-700 hover:text-[#6CA9F5] py-2"
                      >
                        Doctor Enrollment
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (link.onClick) {
                        link.onClick();
                      } else if (link.href) {
                        navigate(link.href);
                      }
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-blue-50 rounded-xl transition-all text-left"
                  >
                    {IconComponent && <IconComponent className="w-4 h-4 text-gray-500" />}
                    <span className="text-sm font-semibold text-gray-800">
                      {link.title}
                    </span>
                  </button>
                )}
              </div>
            );
          })}

          {!user && (
            <div className="pt-4 border-t border-gray-100 flex flex-col gap-2">
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full py-3 text-center text-sm font-semibold text-gray-600 bg-gray-50 rounded-xl"
              >
                Log In
              </Link>
              <Link
                to="/signup"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full py-3 text-center text-sm font-bold text-white bg-blue-600 rounded-xl shadow-lg"
              >
                Create Account
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;